import {
	Album,
	Cart,
	ChevronDown,
	ChevronUp,
	Composer,
	Delete,
	Download as DownloadIcon,
	Ellipsis,
	Folder,
	Mix,
	MusicalNote,
	Notes,
	NotesFilled,
	Star,
	StarFilled,
	Stems,
	WarningLight,
} from '@audio-network/componentlibrary/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import { getAlbumUrl, getComposerUrl, getTrackUrl } from '@/utils/urls'

import AddToProjectsModal from '../../../AddToProjectsModal/AddToProjectsModal'
import Download from '../../../Download'
import EditNotes from '../EditNotes'
import FavouriteButton from '@/components/shared/FavouriteButton/FavouriteButton'
import FlyoutMenu from '../../../FlyoutMenu'
import { Tooltip } from '@audio-network/componentlibrary'
import ViewNotes from '../ViewNotes'
import { addToCart } from '@/services/cartService'
import clsx from 'clsx'
import { hasStemsAccess } from '@/utils/hasStemsAccess'
import { redirectToHref } from '@/utils/window'
import { sendAnalytics } from '@audio-network/analytics'
import styles from './TrackActions.module.css'
import { useDownload } from '../../../Download/Download.hooks'
import useFavouritesStore from '@/stores/favouritesStore'
import { useSession } from '@/context/Session'

type TrackActionsProps = {
	notes?: string
	track: Track
	source: string
	onChangeNotes?: (notes: string, track: Track) => void
	onMoveToTop?: (track: Track) => void
	onMoveToBottom?: (track: Track) => void
	onDelete?: (track: Track) => void
	handleDelete: () => void
}

const TrackActions = ({
	notes,
	track,
	source,
	onChangeNotes,
	onMoveToTop,
	onMoveToBottom,
	onDelete,
	// TODO: Confusing to have both these things
	handleDelete,
}: TrackActionsProps): JSX.Element => {
	const { t } = useTranslation(['trackrow'])
	const { user } = useSession()
	const { handleDownload } = useDownload({
		downloadName: track.title,
		tracks: track,
		trackSource: source,
	})
	const favouriteTracks = useFavouritesStore((state) => state.favouriteTracks)
	const toggleFavourite = useFavouritesStore((state) => state.toggleFavourite)
	const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
	const [isAddToProjectOpen, setIsAddToProjectOpen] = useState(false)
	const [areNotesOpen, setAreNotesOpen] = useState(false)

	useEffect(() => {
		return () => {
			setAreNotesOpen(false)
		}
	}, [])

	//flyout Menu
	const toggleFlyoutMenu = (): void => {
		setIsFlyoutOpen(!isFlyoutOpen)
	}

	const closeFlyoutMenu = useCallback((): void => setIsFlyoutOpen(false), [])

	//favouriting
	const isFavourite = !!favouriteTracks?.some((trackId) => trackId === track.id)

	const handleFavourite = (): void => {
		if (!user?.id) {
			redirectToHref(`/favourites/login?track=${track.id}`)
			return
		}

		sendAnalytics({
			noun: isFavourite ? 'Remove from Favourites' : 'Add to Favourites',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				TrackTitle: track.title,
				TrackId: track.id,
				AlbumNo: track.album.number,
				TrackSource: source,
				Genre: track.album.genre,
				Catalogue: track.album.catalogue.name,
			},
		})

		toggleFavourite(track.id)
	}

	//Add to project
	const toggleIsAddToProjectOpen = (): void => {
		if (!isAddToProjectOpen) {
			sendAnalytics({
				noun: 'Add to Project',
				pastTenseVerb: 'Clicked',
				eventParameters: {
					TrackSource: source,
				},
			})
		}
		setIsAddToProjectOpen(!isAddToProjectOpen)
	}

	//Notes
	const toggleNotesView = (): void => {
		//This only changes on completion
		setAreNotesOpen(!areNotesOpen)
		//So check the inverse as the Notes are clicked if they are currently not open
		if (!areNotesOpen) {
			sendAnalytics({
				noun: 'Project Note',
				pastTenseVerb: 'Clicked',
				eventParameters: { TrackId: track.id },
			})
		}
	}

	//Cart
	const handleAddToCart = (): void => {
		addToCart(user.id, track.id).then((cart) => {
			sendAnalytics({
				noun: 'Add to Cart',
				pastTenseVerb: 'Clicked',
				eventParameters: {
					TrackId: track.id,
					TrackTitle: track.title,
					AlbumNo: track.album.number,
					CartCount: cart.itemsCount,
					TrackSource: source,
					Genre: track.album.genre,
					Catalogue: track.album.catalogue.name,
				},
			})
			redirectToHref('/cart')
		})
	}

	const menuOptionsConfig = [
		{
			Icon: isFavourite ? <StarFilled color="yellow" /> : <Star />,
			title: t('track-actions.favourite-track'),
			'aria-pressed': isFavourite,
			onClick: handleFavourite,
			className: styles.primaryFlyoutMenuAction,
			include: !!user?.id,
		},
		{
			Icon: <Folder />,
			title: t('track-actions.add-to-project'),
			onClick: toggleIsAddToProjectOpen,
			className: styles.primaryFlyoutMenuAction,
			include: !!user?.id,
		},
		{
			Icon: notes?.length ? <NotesFilled /> : <Notes />,
			title: t('track-actions.notes'),
			onClick: toggleNotesView,
			className: styles.primaryFlyoutMenuAction,
			include: !!(onChangeNotes || notes?.length),
		},
		{
			Icon: <ChevronUp />,
			title: t('track-actions.move-to-top'),
			onClick: () => onMoveToTop(track),
			include: !!onMoveToTop,
		},
		{
			Icon: <ChevronDown />,
			title: t('track-actions.move-to-bottom'),
			onClick: () => onMoveToBottom(track),
			include: !!onMoveToBottom,
		},
		{
			Icon: <MusicalNote />,
			title: t('track-actions.go-to-track-details'),
			href: getTrackUrl(track),
			include: true,
		},
		{
			Icon: <Album />,
			title: `${t('album')}: ${track.album.name}`,
			href: getAlbumUrl(track.album),
			include: !!track.album,
		},
		...track.composers.map((composer) => ({
			title: `${composer.name} (${composer.collectionSociety})`,
			Icon: <Composer />,
			href: getComposerUrl(composer),
			include: true,
		})),
		{
			Icon: <Mix />,
			title: `${track.mixCount} mixes available`,
			href: getTrackUrl(track),
			include: track?.mixCount >= 2,
		},
		{
			Icon: <Stems />,
			title: t('track-actions.stems-available'),
			href: `${getTrackUrl(track)}?initialTab=stems`,
			include: hasStemsAccess(user) && track.hasStems,
		},
		{
			Icon: <Cart />,
			title: t('track-actions.add-to-cart'),
			onClick: handleAddToCart,
			className: styles.primaryFlyoutMenuAction,
			include: user?.organisation.canPurchase,
		},
		{
			Icon: <DownloadIcon />,
			title: t('track-actions.download-row-option-mp3'),
			onClick: (event: MouseEvent) => {
				event.preventDefault()
				setIsFlyoutOpen(false)
				handleDownload('mp3')
			},
			className: clsx(styles.primaryFlyoutMenuAction, styles.download),
			include: !!user?.organisation.canDownload,
		},
		{
			Icon: <DownloadIcon />,
			title: t('track-actions.download-row-option-wav'),
			onClick: (event: MouseEvent) => {
				event.preventDefault()
				setIsFlyoutOpen(false)
				handleDownload('wav')
			},
			className: clsx(styles.primaryFlyoutMenuAction, styles.download),
			include: !!user?.organisation.canDownload,
		},
		{
			Icon: <Delete />,
			title: t('track-actions.delete'),
			onClick: handleDelete,
			include: !!onDelete,
		},
	]

	const menuOptions = menuOptionsConfig
		.filter((option) => option.include)
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		.map(({ include, ...option }) => option)

	const canViewTrackCatalogue =
		!user || user.viewableCatalogues.includes(track.album.catalogue.id)

	return (
		<div className={styles.trackActions} data-testid="track-actions">
			{canViewTrackCatalogue && (
				<>
					{(onChangeNotes || typeof notes === 'string') && (
						<div className={styles.trackActionsNotesWrapper}>
							{notes?.length ? (
								<NotesFilled
									title={t('track-notes.notes')}
									className={styles.trackAction}
									disabled={!onChangeNotes && notes.length === 0}
									onClick={toggleNotesView}
								/>
							) : (
								<Notes
									title={t('track-notes.notes')}
									className={styles.trackAction}
									disabled={!onChangeNotes && notes.length === 0}
									onClick={toggleNotesView}
								/>
							)}
							{onChangeNotes && (
								<EditNotes
									isOpen={areNotesOpen}
									notes={notes}
									onChangeNotes={onChangeNotes}
									onClose={toggleNotesView}
									track={track}
								/>
							)}
							{!onChangeNotes && notes?.length > 0 && (
								<ViewNotes
									id="notes"
									isOpen={areNotesOpen}
									notes={notes}
									onClose={toggleNotesView}
								/>
							)}
						</div>
					)}
					{user?.id && (
						<>
							<Folder
								title={t('track-actions.add-to-project')}
								onClick={toggleIsAddToProjectOpen}
								className={styles.trackAction}
							/>
							<AddToProjectsModal
								isOpen={isAddToProjectOpen}
								tracks={[track]}
								onClose={toggleIsAddToProjectOpen}
								source={source}
							/>
						</>
					)}
					{user && (
						<FavouriteButton
							className={styles.trackAction}
							source={source}
							track={track}
						/>
					)}
					{user?.organisation.canPurchase && (
						<Cart
							title={t('track-actions.add-to-cart')}
							className={styles.trackAction}
							onClick={handleAddToCart}
						/>
					)}
					<Download
						className={clsx(styles.trackAction, styles.download)}
						tracks={track}
						trackSource={source}
						downloadText={t('track-actions.download-row-primary')}
						downloadOptions={t('track-actions.download-row-options')}
						downloadOptionWav={t('track-actions.download-row-option-wav')}
						downloadOptionMp3={t('track-actions.download-row-option-mp3')}
					/>
				</>
			)}
			{!canViewTrackCatalogue && (
				<Tooltip
					id={`track-unavailable-${track.id}`}
					content={t('track-actions.track-unavailable')}
				>
					<WarningLight />
				</Tooltip>
			)}

			<Ellipsis
				title={t('track-actions.more-options')}
				aria-controls={`t${track.id}-more-options`}
				aria-expanded={isFlyoutOpen}
				onClick={toggleFlyoutMenu}
			/>

			<FlyoutMenu
				data-testid="track-actions-flyout"
				id={`t${track.id}-more-options`}
				isOpen={isFlyoutOpen}
				onClose={closeFlyoutMenu}
				options={menuOptions}
				notification={
					!canViewTrackCatalogue && {
						Icon: <WarningLight />,
						message: (
							<Trans
								t={t}
								i18nKey="track-actions.track-unavailable-contact-us"
								components={{
									// eslint-disable-next-line jsx-a11y/anchor-has-content
									a: <a href="/contact" />,
								}}
							/>
						),
					}
				}
			/>
		</div>
	)
}

export default TrackActions
